import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Target, Loader2, RefreshCw, Eye, EyeOff, Users, Trophy,
  TrendingUp, Medal, Search
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Switch } from "../ui/switch";

const StatsAdmin = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sectionEnabled, setSectionEnabled] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [activeTab, setActiveTab] = useState("scorers");
  const [topScorers, setTopScorers] = useState([]);
  const [topAppearances, setTopAppearances] = useState([]);
  const [h2hRecords, setH2hRecords] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [teamFilter, setTeamFilter] = useState("all");
  const [refreshingScorers, setRefreshingScorers] = useState(false);
  const [refreshingH2H, setRefreshingH2H] = useState(false);
  const [gameRatings, setGameRatings] = useState([]);
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [ratingForm, setRatingForm] = useState({
    team: "men", opponent: "", competition: "", result: "", date: "", venue: "", scorers: "",
    players: [{ name: "", rating: "", minutes: "", goals: 0, assists: 0 }]
  });
  const [savingRating, setSavingRating] = useState(false);
  const [pendingMatches, setPendingMatches] = useState([]);
  const [detectingMatches, setDetectingMatches] = useState(false);
  const [reviewingMatch, setReviewingMatch] = useState(null);

  useEffect(() => {
    fetchAllStats();
    fetchSectionStatus();
    fetchGameRatings();
    fetchPendingMatches();
  }, []);

  const fetchAllStats = async () => {
    try {
      const [scorersRes, h2hRes] = await Promise.all([
        axios.get(`${API}/players/top-stats`),
        axios.get(`${API}/head-to-head`)
      ]);
      
      // Process top scorers - combine men and women
      const menScorers = (scorersRes.data?.men?.top_scorers || []).map(p => ({...p, team: "Men"}));
      const womenScorers = (scorersRes.data?.women?.top_scorers || []).map(p => ({...p, team: "Women"}));
      setTopScorers([...menScorers, ...womenScorers]);
      
      // Process appearances
      const menAppearances = (scorersRes.data?.men?.top_appearances || []).map(p => ({...p, team: "Men"}));
      const womenAppearances = (scorersRes.data?.women?.top_appearances || []).map(p => ({...p, team: "Women"}));
      setTopAppearances([...menAppearances, ...womenAppearances]);
      
      // H2H records
      setH2hRecords(h2hRes.data?.results || []);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      toast.error("Failed to load stats data");
    } finally {
      setLoading(false);
    }
  };

  const fetchSectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/section-toggles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSectionEnabled(response.data.stats_section_enabled !== false);
    } catch (error) {
      console.error("Failed to fetch section status");
    }
  };

  const toggleSection = async () => {
    setToggling(true);
    try {
      const response = await axios.post(`${API}/admin/section-toggle/stats_section_enabled`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSectionEnabled(response.data.stats_section_enabled);
      toast.success(`Stats section ${response.data.stats_section_enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error("Failed to toggle section");
    } finally {
      setToggling(false);
    }
  };

  const refreshPlayerStats = async () => {
    setRefreshingScorers(true);
    try {
      const response = await axios.get(`${API}/players/scrape-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "Player stats refreshed");
      fetchAllStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to refresh player stats");
    } finally {
      setRefreshingScorers(false);
    }
  };

  const refreshH2HStats = async () => {
    setRefreshingH2H(true);
    try {
      const response = await axios.get(`${API}/head-to-head/scrape`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "H2H records refreshed");
      fetchAllStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to refresh H2H records");
    } finally {
      setRefreshingH2H(false);
    }
  };

  const fetchGameRatings = async () => {
    try {
      const [menRes, womenRes] = await Promise.all([
        axios.get(`${API}/game-ratings?team=men`),
        axios.get(`${API}/game-ratings?team=women`)
      ]);
      setGameRatings([...menRes.data, ...womenRes.data]);
    } catch (error) {
      console.error("Failed to fetch game ratings");
    }
  };

  const addPlayerRow = () => {
    setRatingForm(prev => ({
      ...prev,
      players: [...prev.players, { name: "", rating: "", minutes: "", goals: 0, assists: 0 }]
    }));
  };

  const removePlayerRow = (idx) => {
    setRatingForm(prev => ({
      ...prev,
      players: prev.players.filter((_, i) => i !== idx)
    }));
  };

  const updatePlayerRow = (idx, field, value) => {
    setRatingForm(prev => ({
      ...prev,
      players: prev.players.map((p, i) => i === idx ? { ...p, [field]: value } : p)
    }));
  };

  const saveGameRating = async () => {
    if (!ratingForm.opponent || !ratingForm.date) {
      toast.error("Opponent and date are required");
      return;
    }
    setSavingRating(true);
    try {
      const ratings = ratingForm.players
        .filter(p => p.name && p.rating)
        .map(p => ({
          name: p.name,
          rating: parseFloat(p.rating),
          minutes_played: parseInt(p.minutes) || 0,
          goals: parseInt(p.goals) || 0,
          assists: parseInt(p.assists) || 0,
          position: "Player"
        }));
      
      await axios.post(`${API}/admin/game-ratings`, {
        ...ratingForm,
        ratings
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      toast.success("Game ratings saved");
      setShowRatingForm(false);
      setRatingForm({
        team: "men", opponent: "", competition: "", result: "", date: "", venue: "", scorers: "",
        players: [{ name: "", rating: "", minutes: "", goals: 0, assists: 0 }]
      });
      fetchGameRatings();
    } catch (error) {
      toast.error("Failed to save game ratings");
    } finally {
      setSavingRating(false);
    }
  };

  const deleteGameRating = async (id) => {
    if (!confirm("Delete this game rating?")) return;
    try {
      await axios.delete(`${API}/admin/game-ratings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Game rating deleted");
      fetchGameRatings();
      fetchPendingMatches();
    } catch (error) {
      toast.error("Failed to delete game rating");
    }
  };

  const fetchPendingMatches = async () => {
    try {
      const res = await axios.get(`${API}/admin/auto-ratings/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingMatches(res.data.pending || []);
    } catch (error) {
      console.error("Failed to fetch pending matches");
    }
  };

  const runAutoDetect = async () => {
    setDetectingMatches(true);
    try {
      const res = await axios.post(`${API}/admin/auto-ratings/run`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Detection complete: ${res.data.result?.new_matches || 0} new matches found`);
      fetchPendingMatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Detection failed");
    } finally {
      setDetectingMatches(false);
    }
  };

  const submitReview = async (matchId, ratings, scorers) => {
    try {
      const formattedRatings = ratings
        .filter(r => r.rating > 0)
        .map(r => ({
          name: r.name,
          position: r.position || "Player",
          rating: parseFloat(r.rating),
          minutes_played: parseInt(r.minutes) || 0,
          goals: parseInt(r.goals) || 0,
          assists: parseInt(r.assists) || 0,
        }));
      await axios.put(`${API}/admin/game-ratings/${matchId}/review`, {
        ratings: formattedRatings,
        scorers: scorers,
      }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Ratings submitted and match reviewed");
      setReviewingMatch(null);
      fetchPendingMatches();
      fetchGameRatings();
    } catch (error) {
      toast.error("Failed to submit review");
    }
  };

  // Filter functions
  const filteredScorers = topScorers.filter(p => {
    if (teamFilter !== "all" && p.team !== teamFilter) return false;
    if (searchQuery && !p.name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const filteredAppearances = topAppearances.filter(p => {
    if (teamFilter !== "all" && p.team !== teamFilter) return false;
    if (searchQuery && !p.name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const filteredH2H = h2hRecords.filter(r => {
    if (searchQuery && !r.opponent?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" /></div>;
  }

  return (
    <div data-testid="stats-admin">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
          <Target className="w-8 h-8 inline mr-2 text-[#7C2C3B]" />
          STATS ADMIN
        </h1>
      </div>

      {/* Section Visibility Toggle */}
      <div className={`rounded-xl p-4 mb-6 border-2 ${sectionEnabled ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {sectionEnabled ? <Eye className="w-6 h-6 text-green-600" /> : <EyeOff className="w-6 h-6 text-red-600" />}
            <div>
              <h3 className="font-bold text-slate-800">Stats Section Visibility</h3>
              <p className="text-sm text-slate-600">
                {sectionEnabled ? "Section is VISIBLE on the website" : "Section is HIDDEN from the website"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className={`px-3 py-1 rounded-full text-sm font-bold ${sectionEnabled ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
              {sectionEnabled ? "VISIBLE" : "HIDDEN"}
            </span>
            <Switch
              checked={sectionEnabled}
              onCheckedChange={toggleSection}
              disabled={toggling}
              data-testid="toggle-stats-visibility"
            />
          </div>
        </div>
      </div>

      {/* Stats Overview Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-[#0F172A]">{topScorers.length}</div>
              <div className="text-xs text-slate-500">Top Scorers</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-[#0F172A]">{topAppearances.length}</div>
              <div className="text-xs text-slate-500">Top Appearances</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-[#0F172A]">{h2hRecords.length}</div>
              <div className="text-xs text-slate-500">H2H Records</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Medal className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-[#0F172A]">
                {topScorers.find(p => p.rank === 1)?.goals || '-'}
              </div>
              <div className="text-xs text-slate-500">Record Goals</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="scorers" className="data-[state=active]:bg-[#7C2C3B] data-[state=active]:text-white">
              <Trophy className="w-4 h-4 mr-2" />Top Scorers
            </TabsTrigger>
            <TabsTrigger value="appearances" className="data-[state=active]:bg-[#7C2C3B] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />Top Appearances
            </TabsTrigger>
            <TabsTrigger value="h2h" className="data-[state=active]:bg-[#7C2C3B] data-[state=active]:text-white">
              <TrendingUp className="w-4 h-4 mr-2" />H2H Records
            </TabsTrigger>
            <TabsTrigger value="ratings" className="data-[state=active]:bg-[#7C2C3B] data-[state=active]:text-white" data-testid="game-ratings-tab">
              <Medal className="w-4 h-4 mr-2" />Game Ratings
              {pendingMatches.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-red-500 text-white rounded-full">
                  {pendingMatches.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Refresh Buttons */}
          <div className="flex gap-2">
            {activeTab !== "h2h" && (
              <Button 
                onClick={refreshPlayerStats} 
                disabled={refreshingScorers}
                variant="outline"
                className="border-[#2BAEE5] text-[#2BAEE5]"
                data-testid="refresh-player-stats-btn"
              >
                {refreshingScorers ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Refreshing...</>
                ) : (
                  <><RefreshCw className="w-4 h-4 mr-2" /> Refresh Players</>
                )}
              </Button>
            )}
            {activeTab === "h2h" && (
              <Button 
                onClick={refreshH2HStats} 
                disabled={refreshingH2H}
                variant="outline"
                className="border-[#2BAEE5] text-[#2BAEE5]"
                data-testid="refresh-h2h-btn"
              >
                {refreshingH2H ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Refreshing...</>
                ) : (
                  <><RefreshCw className="w-4 h-4 mr-2" /> Refresh H2H</>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder={activeTab === "h2h" ? "Search opponent..." : "Search player..."} 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          {activeTab !== "h2h" && (
            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger className="w-32"><SelectValue placeholder="Team" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                <SelectItem value="Men">Men</SelectItem>
                <SelectItem value="Women">Women</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Top Scorers Tab */}
        <TabsContent value="scorers">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto max-h-[500px]">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-100 z-10">
                  <TableRow>
                    <TableHead className="w-16 text-center">#</TableHead>
                    <TableHead>Player</TableHead>
                    <TableHead className="w-20">Team</TableHead>
                    <TableHead className="w-24 text-center">Goals</TableHead>
                    <TableHead className="w-24 text-center">Apps</TableHead>
                    <TableHead className="w-24 text-center">Ratio</TableHead>
                    <TableHead>Years</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredScorers.slice(0, 100).map((player, idx) => (
                    <TableRow key={`${player.name}-${idx}`}>
                      <TableCell className="text-center font-bold text-slate-500">{player.rank || idx + 1}</TableCell>
                      <TableCell className="font-medium">{player.name}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded ${player.team === "Men" ? "bg-[#7C2C3B] text-white" : "bg-purple-600 text-white"}`}>
                          {player.team}
                        </span>
                      </TableCell>
                      <TableCell className="text-center font-bold text-amber-600">{player.goals}</TableCell>
                      <TableCell className="text-center">{player.appearances || '-'}</TableCell>
                      <TableCell className="text-center text-slate-500">
                        {player.goals && player.appearances ? (player.goals / player.appearances).toFixed(2) : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{player.years || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {filteredScorers.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Trophy className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No top scorers found</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Top Appearances Tab */}
        <TabsContent value="appearances">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto max-h-[500px]">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-100 z-10">
                  <TableRow>
                    <TableHead className="w-16 text-center">#</TableHead>
                    <TableHead>Player</TableHead>
                    <TableHead className="w-20">Team</TableHead>
                    <TableHead className="w-24 text-center">Apps</TableHead>
                    <TableHead className="w-24 text-center">Goals</TableHead>
                    <TableHead>Years</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAppearances.slice(0, 100).map((player, idx) => (
                    <TableRow key={`${player.name}-${idx}`}>
                      <TableCell className="text-center font-bold text-slate-500">{player.rank || idx + 1}</TableCell>
                      <TableCell className="font-medium">{player.name}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded ${player.team === "Men" ? "bg-[#7C2C3B] text-white" : "bg-purple-600 text-white"}`}>
                          {player.team}
                        </span>
                      </TableCell>
                      <TableCell className="text-center font-bold text-blue-600">{player.appearances}</TableCell>
                      <TableCell className="text-center">{player.goals || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-500">{player.years || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {filteredAppearances.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No appearances data found</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* H2H Records Tab */}
        <TabsContent value="h2h">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto max-h-[500px]">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-100 z-10">
                  <TableRow>
                    <TableHead>Opponent</TableHead>
                    <TableHead className="w-20 text-center">Played</TableHead>
                    <TableHead className="w-20 text-center text-green-600">Won</TableHead>
                    <TableHead className="w-20 text-center">Drawn</TableHead>
                    <TableHead className="w-20 text-center text-red-600">Lost</TableHead>
                    <TableHead className="w-20 text-center">GF</TableHead>
                    <TableHead className="w-20 text-center">GA</TableHead>
                    <TableHead className="w-24 text-center">Win %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredH2H.slice(0, 100).map((record, idx) => (
                    <TableRow key={`${record.opponent}-${idx}`}>
                      <TableCell className="font-medium">{record.opponent}</TableCell>
                      <TableCell className="text-center">{record.played}</TableCell>
                      <TableCell className="text-center font-bold text-green-600">{record.won}</TableCell>
                      <TableCell className="text-center text-slate-500">{record.drawn}</TableCell>
                      <TableCell className="text-center font-bold text-red-600">{record.lost}</TableCell>
                      <TableCell className="text-center">{record.goals_for}</TableCell>
                      <TableCell className="text-center">{record.goals_against}</TableCell>
                      <TableCell className="text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          (record.won / record.played * 100) >= 50 ? 'bg-green-100 text-green-700' : 
                          (record.won / record.played * 100) >= 30 ? 'bg-amber-100 text-amber-700' : 
                          'bg-red-100 text-red-700'
                        }`}>
                          {((record.won / record.played) * 100).toFixed(1)}%
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {filteredH2H.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No H2H records found. Click "Refresh H2H" to fetch data.</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Game Ratings Tab */}
        <TabsContent value="ratings">
          <div className="space-y-4">
            {/* Auto-Detected Pending Matches */}
            {pendingMatches.length > 0 && (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4" data-testid="pending-matches-section">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-amber-500 rounded-full animate-pulse"></div>
                    <h4 className="font-bold text-amber-800">
                      {pendingMatches.length} Match{pendingMatches.length > 1 ? 'es' : ''} Awaiting Ratings
                    </h4>
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={runAutoDetect}
                    disabled={detectingMatches}
                    className="border-amber-400 text-amber-700 text-xs"
                    data-testid="detect-matches-btn"
                  >
                    {detectingMatches ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    Detect New Matches
                  </Button>
                </div>
                <div className="space-y-2">
                  {pendingMatches.map(m => (
                    <div key={m.id} className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-amber-200">
                      <div>
                        <span className="font-semibold text-slate-800">{m.opponent}</span>
                        <span className="text-sm text-slate-500 ml-2">{m.competition}</span>
                        <span className="text-xs text-slate-400 ml-2">{m.date}</span>
                        <span className="ml-2 text-sm font-mono">{m.result}</span>
                        <span className="ml-2 text-xs text-slate-400">{m.venue}</span>
                      </div>
                      <Button
                        size="sm"
                        className="bg-[#7C2C3B] hover:bg-[#63232F] text-xs"
                        onClick={() => setReviewingMatch(m)}
                        data-testid={`review-match-${m.id}`}
                      >
                        Add Ratings
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Review Match Dialog */}
            {reviewingMatch && (
              <div className="bg-white rounded-xl border-2 border-[#7C2C3B] p-6 space-y-4" data-testid="review-match-form">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-lg">
                    Rate Players: {reviewingMatch.opponent} ({reviewingMatch.result})
                  </h4>
                  <Button size="sm" variant="ghost" onClick={() => setReviewingMatch(null)}>Close</Button>
                </div>
                <p className="text-sm text-slate-500">
                  {reviewingMatch.competition} | {reviewingMatch.date} | {reviewingMatch.venue}
                </p>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Goal Scorers</label>
                  <Input
                    value={reviewingMatch.scorers || ""}
                    onChange={(e) => setReviewingMatch(prev => ({...prev, scorers: e.target.value}))}
                    placeholder="e.g. Bowen 23', Kudus 67'"
                  />
                </div>
                <div className="max-h-80 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Player</TableHead>
                        <TableHead className="w-16">Pos</TableHead>
                        <TableHead className="w-20">Rating</TableHead>
                        <TableHead className="w-16">Mins</TableHead>
                        <TableHead className="w-14">G</TableHead>
                        <TableHead className="w-14">A</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(reviewingMatch.ratings || []).map((p, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm font-medium">{p.name}</TableCell>
                          <TableCell className="text-xs text-slate-500">{p.position}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.1" min="0" max="10"
                              value={p.rating || ""}
                              onChange={(e) => {
                                const updated = [...reviewingMatch.ratings];
                                updated[idx] = {...updated[idx], rating: e.target.value};
                                setReviewingMatch(prev => ({...prev, ratings: updated}));
                              }}
                              className="h-7 text-sm w-16"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number" min="0" max="120"
                              value={p.minutes || ""}
                              onChange={(e) => {
                                const updated = [...reviewingMatch.ratings];
                                updated[idx] = {...updated[idx], minutes: e.target.value};
                                setReviewingMatch(prev => ({...prev, ratings: updated}));
                              }}
                              className="h-7 text-sm w-14"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number" min="0"
                              value={p.goals || 0}
                              onChange={(e) => {
                                const updated = [...reviewingMatch.ratings];
                                updated[idx] = {...updated[idx], goals: e.target.value};
                                setReviewingMatch(prev => ({...prev, ratings: updated}));
                              }}
                              className="h-7 text-sm w-12"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number" min="0"
                              value={p.assists || 0}
                              onChange={(e) => {
                                const updated = [...reviewingMatch.ratings];
                                updated[idx] = {...updated[idx], assists: e.target.value};
                                setReviewingMatch(prev => ({...prev, ratings: updated}));
                              }}
                              className="h-7 text-sm w-12"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setReviewingMatch(null)}>Cancel</Button>
                  <Button
                    className="bg-[#7C2C3B] hover:bg-[#63232F]"
                    onClick={() => submitReview(reviewingMatch.id, reviewingMatch.ratings, reviewingMatch.scorers)}
                    data-testid="submit-review-btn"
                  >
                    Submit Ratings
                  </Button>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center">
              <h3 className="font-bold text-lg">Last Game Ratings</h3>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline"
                  onClick={runAutoDetect}
                  disabled={detectingMatches}
                  className="border-[#2BAEE5] text-[#2BAEE5]"
                  data-testid="run-auto-detect-btn"
                >
                  {detectingMatches ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Auto-Detect Matches
                </Button>
                <Button onClick={() => setShowRatingForm(!showRatingForm)} className="bg-[#7C2C3B] hover:bg-[#63232F]" data-testid="add-game-rating-btn">
                  {showRatingForm ? "Cancel" : "+ Add Game Ratings"}
                </Button>
              </div>
            </div>

            {showRatingForm && (
              <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
                <h4 className="font-bold text-slate-700">Match Details</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Team</label>
                    <Select value={ratingForm.team} onValueChange={(v) => setRatingForm(prev => ({...prev, team: v}))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="men">Men</SelectItem>
                        <SelectItem value="women">Women</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Opponent *</label>
                    <Input value={ratingForm.opponent} onChange={(e) => setRatingForm(prev => ({...prev, opponent: e.target.value}))} placeholder="e.g. Liverpool" data-testid="rating-opponent-input" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Competition</label>
                    <Input value={ratingForm.competition} onChange={(e) => setRatingForm(prev => ({...prev, competition: e.target.value}))} placeholder="e.g. Premier League" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Date *</label>
                    <Input type="date" value={ratingForm.date} onChange={(e) => setRatingForm(prev => ({...prev, date: e.target.value}))} data-testid="rating-date-input" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Result</label>
                    <Input value={ratingForm.result} onChange={(e) => setRatingForm(prev => ({...prev, result: e.target.value}))} placeholder="e.g. 2-1" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Venue</label>
                    <Input value={ratingForm.venue} onChange={(e) => setRatingForm(prev => ({...prev, venue: e.target.value}))} placeholder="e.g. London Stadium" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-slate-500 mb-1 block">Scorers</label>
                    <Input value={ratingForm.scorers} onChange={(e) => setRatingForm(prev => ({...prev, scorers: e.target.value}))} placeholder="e.g. Bowen 23', Kudus 67'" />
                  </div>
                </div>

                <h4 className="font-bold text-slate-700 pt-2">Player Ratings</h4>
                <div className="space-y-2">
                  {ratingForm.players.map((player, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input className="flex-1" value={player.name} onChange={(e) => updatePlayerRow(idx, "name", e.target.value)} placeholder="Player name" />
                      <Input className="w-20" type="number" step="0.1" min="1" max="10" value={player.rating} onChange={(e) => updatePlayerRow(idx, "rating", e.target.value)} placeholder="Rating" />
                      <Input className="w-20" type="number" value={player.minutes} onChange={(e) => updatePlayerRow(idx, "minutes", e.target.value)} placeholder="Mins" />
                      <Input className="w-16" type="number" value={player.goals} onChange={(e) => updatePlayerRow(idx, "goals", e.target.value)} placeholder="G" />
                      <Input className="w-16" type="number" value={player.assists} onChange={(e) => updatePlayerRow(idx, "assists", e.target.value)} placeholder="A" />
                      {ratingForm.players.length > 1 && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removePlayerRow(idx)}>×</Button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={addPlayerRow}>+ Add Player</Button>
                  <Button className="bg-[#7C2C3B] hover:bg-[#63232F]" onClick={saveGameRating} disabled={savingRating} data-testid="save-game-rating-btn">
                    {savingRating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : "Save Ratings"}
                  </Button>
                </div>
              </div>
            )}

            {/* Saved Game Ratings */}
            <div className="space-y-3">
              {gameRatings.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
                  <Medal className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No game ratings entered yet. Click "Add Game Ratings" to start.</p>
                </div>
              ) : (
                gameRatings.map(game => (
                  <div key={game.id} className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${game.team === "men" ? "bg-[#7C2C3B] text-white" : "bg-purple-600 text-white"}`}>
                            {game.team === "men" ? "MEN" : "WOMEN"}
                          </span>
                          <span className="font-bold text-slate-800">vs {game.opponent}</span>
                          <span className="text-sm text-slate-500">{game.result}</span>
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          {game.competition} | {game.date} | {game.venue}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteGameRating(game.id)} data-testid={`delete-rating-${game.id}`}>Delete</Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {(game.ratings || []).sort((a, b) => b.rating - a.rating).map((p, i) => (
                        <div key={i} className="flex items-center justify-between bg-slate-50 rounded px-3 py-1.5 text-sm">
                          <span className="truncate mr-2">{p.name}</span>
                          <span className={`font-bold ${p.rating >= 7.5 ? "text-green-600" : p.rating >= 6.5 ? "text-amber-600" : "text-red-600"}`}>{p.rating}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StatsAdmin;
